import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import L from 'leaflet';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { LanguageProvider } from './i18n/LanguageContext';
import { ThemeProvider } from './theme/ThemeContext';
import './theme.css';

// Remove "Leaflet" prefix from map attribution — keeps OSM credit visible
L.Control.Attribution.prototype.options.prefix = false;

// Brand demo (temporary — Phase 80-03 visual verification)
const BrandDemo = React.lazy(() => import('./components/brand/__demo__/BrandDemo'));

// Pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const OAuthCompletePage = React.lazy(() => import('./pages/OAuthCompletePage'));
// HomePage kept as file (not deleted) — may be referenced by other future routes
const ChatLandingPage = React.lazy(() => import('./pages/ChatLandingPage'));
const PlanningPage = React.lazy(() => import('./pages/PlanningPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const AdminDataSourcesPage = React.lazy(() => import('./pages/AdminDataSourcesPage'));
const PoiDetailPage = React.lazy(() => import('./pages/PoiDetailPage'));
const DataSourceDetailPage = React.lazy(() => import('./pages/DataSourceDetailPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SavedRoutesPage = React.lazy(() => import('./pages/SavedRoutesPage'));
const MyTripsStubPage = React.lazy(() => import('./pages/MyTripsStubPage'));
const FavoritesPage = React.lazy(() => import('./pages/FavoritesPage'));
const ComparisonPage = React.lazy(() => import('./pages/ComparisonPage'));
const AdminContentInputPage = React.lazy(() => import('./pages/AdminContentInputPage'));
const HarvestJobListPage = React.lazy(() => import('./pages/HarvestJobListPage'));
const HarvestJobDetailPage = React.lazy(() => import('./pages/HarvestJobDetailPage'));
const AdminGpxPage = React.lazy(() => import('./pages/admin/AdminGpxPage'));
const PoiManagementPage = React.lazy(() => import('./pages/admin/PoiManagementPage'));
const PublicPoiPage = React.lazy(() => import('./pages/PublicPoiPage'));
const WalkRoutesPage = React.lazy(() => import('./pages/WalkRoutesPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const BookingPage = React.lazy(() => import('./pages/BookingPage'));
const AdminGuidesPage = React.lazy(() => import('./pages/admin/AdminGuidesPage'));
const PoiDiscoveryPage = React.lazy(() => import('./pages/admin/PoiDiscoveryPage'));
const GuideBookingsPage = React.lazy(() => import('./pages/GuideBookingsPage'));
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));

// Guide cabinet (Phase 63) — nested routes under /guide/cabinet
const GuideLayout = React.lazy(() => import('./components/guide/GuideLayout'));
const GuideCabinetPage = React.lazy(() => import('./pages/guide/GuideCabinetPage'));
// Placeholder stub — real implementation in Plan 04
const GuideRoutesListPage = React.lazy(() => import('./pages/guide/GuideRoutesListPage'));

/**
 * Main application component with routing.
 */
function App() {
  return (
    <LanguageProvider>
      <Router>
        <ThemeProvider>
        <React.Suspense fallback={<div>...</div>}>
        <Routes>
          {/* Brand demo (temporary — Phase 80-03 visual verification) */}
          <Route path="/__brand-demo" element={<BrandDemo />} />
          {/* Public routes */}
          <Route path="/" element={<ChatLandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/oauth/complete" element={<OAuthCompletePage />} />

          {/* Protected routes */}
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <PlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-routes"
            element={
              <ProtectedRoute>
                <SavedRoutesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-trips"
            element={
              <ProtectedRoute>
                <MyTripsStubPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compare"
            element={
              <ProtectedRoute>
                <ComparisonPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planner"
            element={<Navigate to="/planning" replace />}
          />
          <Route
            path="/poi/:id"
            element={
              <ProtectedRoute>
                <PublicPoiPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/walk-routes"
            element={
              <ProtectedRoute>
                <WalkRoutesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/booking/:routeId"
            element={
              <ProtectedRoute>
                <BookingPage />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <AdminPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          {/* /admin/users removed — users tab is at /admin?tab=users */}
          <Route
            path="/admin/data-sources"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <AdminDataSourcesPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/poi/:id"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <PoiDetailPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/data-source/:id"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <DataSourceDetailPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/harvest-jobs"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <HarvestJobListPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/harvest-jobs/:id"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <HarvestJobDetailPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/harvest-jobs/:id/input"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <AdminContentInputPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/gpx"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <AdminGpxPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pois"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <PoiManagementPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/guides"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <AdminGuidesPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          {/* Area-based POI discovery — capability-gated in PoiDiscoveryPage itself */}
          <Route
            path="/admin/poi-discovery"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <PoiDiscoveryPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* Guide routes */}
          <Route
            path="/guide/bookings"
            element={
              <ProtectedRoute requireGuide>
                <GuideBookingsPage />
              </ProtectedRoute>
            }
          />

          {/* Guide cabinet (Phase 63) — nested under GuideLayout shell */}
          <Route
            path="/guide/cabinet"
            element={
              <ProtectedRoute requireGuide>
                <GuideLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<GuideCabinetPage />} />
            <Route path="routes" element={<GuideRoutesListPage />} />
            {/* "My Bookings" link in the sidebar redirects to the existing page */}
            <Route path="bookings" element={<Navigate to="/guide/bookings" replace />} />
          </Route>

          {/* 404 page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        {/* Global toast notifications */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        </React.Suspense>
        </ThemeProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App;
